"use client";

import { Fragment, ReactNode, useEffect, useMemo, useRef } from "react";
import dynamic from "next/dynamic";
import NextImage from "next/image";
import { stegaClean } from "@sanity/client/stega";
import { cn } from "@/lib/utils";
import { IElitePlatform } from "@/sanity/pages/PageEliteTemplate";
import { CAP_TRIM, DISPLAY, GRADIENT_TRIM_PAD, EliteChip, gradientText } from "./EliteShared";

const EliteMedal = dynamic(() => import("./EliteMedal"), { ssr: false });

/** Resting opacity of a word before the scroll reaches it (Figma: 16%; platforms 10%). */
const DIM_WORD = 0.16;
const DIM_PLATFORMS = 0.1;
/** How many words fade in at once. */
const WINDOW = 2;

type Token = { kind: "word"; text: string } | { kind: "medal" } | { kind: "platforms" };

function tokenize(sentence: string): Token[] {
  return sentence.split(/(\{medal\}|\{platforms\})/).flatMap<Token>((part) => {
    if (part === "{medal}") return [{ kind: "medal" }];
    if (part === "{platforms}") return [{ kind: "platforms" }];
    return part
      .split(/\s+/)
      .filter(Boolean)
      .map((text) => ({ kind: "word", text }));
  });
}

const smooth = (x: number) => {
  const t = Math.max(0, Math.min(1, x));
  return t * t * (3 - 2 * t);
};

export default function EliteProgramme({
  chip,
  title,
  sentence,
  platforms,
}: {
  chip?: string;
  title?: string;
  sentence?: string;
  platforms?: IElitePlatform[];
}) {
  const tokens = useMemo(() => tokenize(stegaClean(sentence ?? "")), [sentence]);
  const sentenceRef = useRef<HTMLParagraphElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  // Lights the sentence word by word as it crosses the viewport (the prototype's "v1").
  useEffect(() => {
    const el = sentenceRef.current;
    if (!el) return;
    const items = itemRefs.current.filter(Boolean) as HTMLElement[];
    const paint = (progress: number) => {
      const front = progress * (items.length + WINDOW);
      items.forEach((item, i) => {
        const min = item.dataset.dim ? Number(item.dataset.dim) : DIM_WORD;
        item.style.opacity = (min + (1 - min) * smooth((front - i) / WINDOW)).toFixed(3);
      });
    };

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      paint(1);
      return;
    }

    let current = 0;
    let raf = 0;
    const frame = () => {
      const r = el.getBoundingClientRect();
      const target = Math.max(0, Math.min(1, (innerHeight * 0.92 - r.top) / (innerHeight * 0.5)));
      current += (target - current) * 0.22;
      paint(current);
      raf = requestAnimationFrame(frame);
    };
    const io = new IntersectionObserver(([entry]) => {
      cancelAnimationFrame(raf);
      if (entry.isIntersecting) raf = requestAnimationFrame(frame);
    });
    io.observe(el);
    paint(0);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [tokens]);

  let index = 0;
  const register = (node: HTMLElement | null, i: number) => {
    itemRefs.current[i] = node;
  };

  const renderToken = (token: Token): ReactNode => {
    const i = index++;
    if (token.kind === "word") {
      return (
        <span
          ref={(n) => register(n, i)}
          className={cn(DISPLAY, "transition-none")}
          style={{ opacity: DIM_WORD }}>
          {token.text}
        </span>
      );
    }
    if (token.kind === "medal") {
      return (
        <span
          ref={(n) => register(n, i)}
          style={{ opacity: DIM_WORD }}
          className="relative -my-[0.5rem] inline-block h-[3.667rem] w-[3.178rem] align-middle lg:mr-[1.5rem] lg:h-[5.5446rem] lg:w-[4.8054rem]">
          <EliteMedal />
        </span>
      );
    }
    if (!platforms?.length) return null;
    return (
      <span
        ref={(n) => register(n, i)}
        data-dim={DIM_PLATFORMS}
        style={{ opacity: DIM_PLATFORMS }}
        className="inline-flex items-center align-middle">
        {platforms.map((platform, p) => (
          <span
            key={platform._key ?? p}
            title={platform.name}
            className="relative h-[3.8473rem] w-[3.8473rem] shrink-0 overflow-hidden rounded-full shadow-[0_0_0_0.2263rem_black] lg:h-[4.713rem] lg:w-[4.713rem] lg:shadow-[0_0_0_0.2773rem_black] [&:not(:last-child)]:mr-[-0.6789rem] lg:[&:not(:last-child)]:mr-[-0.8318rem]">
            {platform.logo?.src && (
              <NextImage
                src={platform.logo.src}
                alt={platform.name ?? ""}
                fill
                sizes="48px"
                quality={100}
                className="object-cover"
              />
            )}
          </span>
        ))}
      </span>
    );
  };

  return (
    <div className="flex w-full flex-col items-center gap-[3rem] border-b border-white/10 pb-[5rem] lg:gap-[4rem] lg:pb-[10rem]">
      <div className="flex flex-col items-center gap-[2.25rem] lg:gap-[3.25rem]">
        <EliteChip
          label={chip}
          variant="solid"
        />
        {title && (
          <h2
            className={cn(
              DISPLAY,
              CAP_TRIM,
              GRADIENT_TRIM_PAD,
              "w-[36rem] text-center text-[4.5rem] leading-[0.94] tracking-[-0.135rem] lg:w-[108.25rem] lg:text-[6.5rem] lg:tracking-[-0.195rem]",
            )}
            style={gradientText(95.02, "#fff", "82.79%", "rgba(39,39,39,0.7)", "110.15%")}>
            {title}
          </h2>
        )}
      </div>
      {tokens.length > 0 && (
        <p
          ref={sentenceRef}
          aria-label={stegaClean(sentence ?? "")
            .replace(/\{medal\}|\{platforms\}/g, "")
            .replace(/\s+/g, " ")
            .trim()}
          className="m-0 w-[47.75rem] text-center text-[2.75rem] leading-[1.24] tracking-[-0.0688rem] text-white [word-spacing:0.275rem] lg:w-[103.5rem] lg:text-[4rem] lg:leading-[5.0419rem] lg:tracking-[-0.1rem] lg:[word-spacing:normal]">
          <span
            aria-hidden="true"
            className={DISPLAY}>
            {tokens.map((token, t) => (
              <Fragment key={t}>
                {t > 0 && " "}
                {renderToken(token)}
              </Fragment>
            ))}
          </span>
        </p>
      )}
    </div>
  );
}
